const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const TOKEN_KEY = 'geniusmind_token';
// Render's free tier spins the server down after inactivity; the first
// request after that can take 30-60s to wake it back up.
const REQUEST_TIMEOUT_MS = 60000;

let isRefreshing = false;
let refreshQueue = [];

export function getToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function removeToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// The server always responds with JSON, but a crashed serverless function or
// a proxy in front of it can return an HTML error page instead — parsing
// that as JSON throws a cryptic "Unexpected token <" error, so fall back to
// a message built from the raw status.
async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return { error: `Unexpected response from server (status ${res.status})` };
  }
}

async function timedFetch(url, options) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
  const externalSignal = options.signal;
  if (externalSignal) {
    if (externalSignal.aborted) controller.abort();
    else externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (err) {
    if (err.name === 'AbortError') {
      // Caller cancelled on purpose (e.g. a stale autocomplete request) —
      // let it propagate as-is instead of the timeout's user-facing message.
      if (externalSignal?.aborted) throw err;
      throw { status: 0, message: 'The server took too long to respond. Please try again.' };
    }
    throw { status: 0, message: 'Network error — check your connection and try again.' };
  } finally {
    clearTimeout(timer);
  }
}

async function request(endpoint, options = {}) {
  const { method = 'GET', body, headers: extraHeaders = {}, _retry, signal } = options;

  const isFormData = body instanceof FormData;
  const headers = isFormData ? { ...extraHeaders } : { 'Content-Type': 'application/json', ...extraHeaders };

  const token = getToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await timedFetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
    credentials: 'include',
    signal,
  });

  if (res.status === 401 && !_retry && endpoint !== '/auth/login' && endpoint !== '/auth/refresh' && endpoint !== '/auth/logout') {
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(() => request(endpoint, { ...options, _retry: true }));
    }

    isRefreshing = true;
    try {
      const refreshRes = await timedFetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      if (refreshRes.ok) {
        const data = await safeJson(refreshRes);
        if (data.accessToken) {
          setToken(data.accessToken);
          refreshQueue.forEach((p) => p.resolve());
          refreshQueue = [];
          headers['Authorization'] = `Bearer ${data.accessToken}`;
          const retryRes = await timedFetch(`${API_BASE}${endpoint}`, {
            method,
            headers,
            body: isFormData ? body : (body ? JSON.stringify(body) : undefined),
            credentials: 'include',
          });
          const retryData = await safeJson(retryRes);
          if (!retryRes.ok) {
            throw { status: retryRes.status, message: retryData.error || 'Something went wrong', data: retryData };
          }
          return retryData;
        }
      }
      refreshQueue.forEach((p) => p.reject(new Error('Session expired')));
      refreshQueue = [];
      removeToken();
      window.dispatchEvent(new CustomEvent('auth:session-expired'));
      throw { status: 401, message: 'Session expired' };
    } catch (err) {
      refreshQueue.forEach((p) => p.reject(err));
      refreshQueue = [];
      throw err;
    } finally {
      isRefreshing = false;
    }
  }

  const data = await safeJson(res);
  if (!res.ok) {
    throw {
      status: res.status,
      message: data.error || data.message || 'Something went wrong',
      data,
    };
  }
  return data;
}

async function requestBlob(endpoint) {
  const token = getToken();
  const res = await timedFetch(`${API_BASE}${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) {
    const data = await safeJson(res);
    throw { status: res.status, message: data.error || 'Failed to download file', data };
  }
  return res.blob();
}

export const api = {
  get: (url, options) => request(url, options),
  getBlob: (url) => requestBlob(url),
  post: (url, body, options) => request(url, { method: 'POST', body, ...options }),
  put: (url, body, options) => request(url, { method: 'PUT', body, ...options }),
  patch: (url, body, options) => request(url, { method: 'PATCH', body, ...options }),
  delete: (url, options) => request(url, { method: 'DELETE', ...options }),
};

// Pings the health endpoint, which also wakes a sleeping Render free-tier
// instance as early as possible so it's often already warm by the time the
// user submits a form. Never throws — resolves false on any failure.
export async function checkBackendHealth() {
  try {
    const res = await fetch(`${API_BASE}/health`);
    return res.ok;
  } catch {
    return false;
  }
}
